import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronRight } from "lucide-react";
import { forwardRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface CollapseItemType {
	key: string;
	label: React.ReactNode;
	children: React.ReactNode;
	extra?: React.ReactNode;
	showArrow?: boolean;
	collapsible?: "header" | "icon" | "disabled";
}

export interface CollapseProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	items?: CollapseItemType[];
	defaultActiveKey?: string | string[];
	activeKey?: string | string[];
	onChange?: (key: string | string[]) => void;
	accordion?: boolean;
	bordered?: boolean;
	ghost?: boolean;
	expandIcon?: (props: { isActive: boolean }) => React.ReactNode;
	expandIconPosition?: "start" | "end";
	size?: "small" | "middle" | "large";
	destroyInactivePanel?: boolean;
}

const Collapse = forwardRef<HTMLDivElement, CollapseProps>(
	(
		{
			className,
			items = [],
			defaultActiveKey,
			activeKey,
			onChange,
			accordion = false,
			bordered = true,
			ghost = false,
			expandIcon,
			expandIconPosition = "start",
			size = "middle",
			destroyInactivePanel = false,
			...props
		},
		ref,
	) => {
		const normalizeKeys = (keys: string | string[] | undefined): string[] => {
			if (!keys) return [];
			return Array.isArray(keys) ? keys : [keys];
		};

		const [internalActiveKeys, setInternalActiveKeys] = useState<string[]>(
			normalizeKeys(defaultActiveKey),
		);

		const currentActiveKeys =
			activeKey !== undefined ? normalizeKeys(activeKey) : internalActiveKeys;

		const handlePanelClick = (key: string, collapsible?: string) => {
			if (collapsible === "disabled") return;

			let newKeys: string[];
			if (accordion) {
				newKeys = currentActiveKeys.includes(key) ? [] : [key];
			} else {
				newKeys = currentActiveKeys.includes(key)
					? currentActiveKeys.filter((k) => k !== key)
					: [...currentActiveKeys, key];
			}

			if (activeKey === undefined) {
				setInternalActiveKeys(newKeys);
			}
			onChange?.(accordion ? newKeys[0] || "" : newKeys);
		};

		const sizeStyles = {
			small: "text-sm",
			middle: "text-sm",
			large: "text-base",
		};

		const headerPadding = {
			small: "px-3 py-2",
			middle: "px-4 py-3",
			large: "px-5 py-4",
		};

		const contentPadding = {
			small: "px-3 py-2",
			middle: "px-4 py-3",
			large: "px-5 py-4",
		};

		const renderExpandIcon = (isActive: boolean) => {
			if (expandIcon) {
				return expandIcon({ isActive });
			}
			return (
				<ChevronRight
					className={cn(
						"h-4 w-4 text-zinc-400 transition-transform duration-200",
						isActive && "rotate-90",
					)}
				/>
			);
		};

		return (
			<div
				ref={ref}
				className={cn(
					sizeStyles[size],
					bordered && !ghost && "border border-zinc-200 dark:border-zinc-700 rounded-lg",
					className,
				)}
				{...props}
			>
				{items.map((item, index) => {
					const isActive = currentActiveKeys.includes(item.key);
					const showArrow = item.showArrow !== false;
					const isDisabled = item.collapsible === "disabled";

					return (
						<Disclosure
							key={item.key}
							as="div"
							defaultOpen={isActive}
							className={cn(
								bordered && !ghost && index > 0 && "border-t border-zinc-200 dark:border-zinc-700",
								ghost && "mb-2",
							)}
						>
							{({ open: _open }) => {
								// Use controlled state instead of Disclosure's internal state
								const panelOpen = isActive;

								return (
									<>
										{/* Header */}
										<DisclosureButton
											as="div"
											onClick={() =>
												item.collapsible !== "icon" && handlePanelClick(item.key, item.collapsible)
											}
											className={cn(
												"flex items-center gap-2 transition-colors w-full text-left",
												headerPadding[size],
												!ghost && "bg-zinc-50 dark:bg-zinc-900/50",
												ghost && "bg-zinc-100 dark:bg-zinc-800 rounded-lg",
												isDisabled
													? "cursor-not-allowed opacity-50"
													: "cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800",
												expandIconPosition === "end" && "flex-row-reverse justify-between",
											)}
										>
											{showArrow && (
												<span
													onClick={(e) => {
														if (item.collapsible === "icon") {
															e.stopPropagation();
															handlePanelClick(item.key, item.collapsible);
														}
													}}
													className={item.collapsible === "icon" ? "cursor-pointer" : ""}
												>
													{renderExpandIcon(panelOpen)}
												</span>
											)}
											<span className="flex-1 font-medium text-zinc-700 dark:text-zinc-200">
												{item.label}
											</span>
											{item.extra && <span onClick={(e) => e.stopPropagation()}>{item.extra}</span>}
										</DisclosureButton>

										{/* Content */}
										{(panelOpen || !destroyInactivePanel) && (
											<DisclosurePanel
												static
												className={cn(
													"overflow-hidden transition-all duration-200",
													panelOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0",
												)}
											>
												<div
													className={cn(
														contentPadding[size],
														"text-zinc-600 dark:text-zinc-300",
														bordered && "border-t border-zinc-200 dark:border-zinc-700",
													)}
												>
													{item.children}
												</div>
											</DisclosurePanel>
										)}
									</>
								);
							}}
						</Disclosure>
					);
				})}
			</div>
		);
	},
);

Collapse.displayName = "Collapse";

export { Collapse };
